import { observer } from 'mobx-react-lite'
import {
  Avatar,
  Button,
  Checkbox,
  Divider,
  Icon,
  Radio,
  Scrollbar,
  SpinnerLoader,
  Switch,
  Tooltip,
  Typography,
} from 'shared/ui'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { type ReviewPermissionsStore } from '../store/reviewPermissionsStore'
import styles from './styles.module.scss'

export const ReviewPermissionsModalContent = observer(
  ({ store }: { store: ReviewPermissionsStore }) => {
    const {
      accessRequest,
      loading,
      initLoading,
      role,
      roleItems,
      setRole,
      useOrganizationSettings,
      setUseOrganizationSettings,
      permissionsArray,
      contactAccessValue,
      setPermission,
      closeModal,
      openRejectModal,
      onUpdate,
      submitButtonText,
    } = store

    if (!accessRequest) return null

    const { user } = accessRequest

    if (initLoading) {
      return (
        <div className={styles.container}>
          <SpinnerLoader ariaLabel='ReviewPermissionsModalContent' />
        </div>
      )
    }

    return (
      <div className={styles.container}>
        <Scrollbar>
          <div className={styles.content}>
            <Divider className={styles.divider} />
            <div className={styles.userInfo}>
              <Avatar info={user.avatarInfo} size={40} />
              <div className={styles.userDetails}>
                <Typography
                  ariaLabel='userName'
                  variant='body-lg-medium'
                  color='var(--content-primary-primary)'
                >
                  {user.fullName}
                </Typography>
                <Typography
                  ariaLabel='userEmail'
                  variant='body-sm-regular'
                  color='var(--content-primary-tertiary)'
                >
                  {user.email}
                </Typography>
              </div>
            </div>

            <Divider className={styles.divider} />

            <div className={styles.roleSection}>
              <div className={styles.labelWrap}>
                <Typography
                  ariaLabel='roleLabel'
                  variant='body-lg-medium'
                  color='var(--content-primary-primary)'
                >
                  Role
                </Typography>
                <Typography
                  ariaLabel='roleDescription'
                  variant='body-md-regular'
                  color='var(--content-primary-tertiary)'
                >
                  Define member role and permissions
                </Typography>
              </div>
              <SelectField
                boxWidth='105px'
                value={role}
                size='medium'
                setValue={setRole}
                disabled={loading}
                withSearch={false}
                dropdownProps={{
                  width: '120px',
                  propsDropdownList: {
                    className: styles.dropdownList,
                  },
                }}
                items={roleItems}
              />
            </div>

            <Divider className={styles.divider} />

            <div className={styles.permissionsHeader}>
              <Typography
                ariaLabel='permissionsLabel'
                variant='body-lg-medium'
                color='var(--content-primary-primary)'
              >
                Permissions
              </Typography>
              <div className={styles.organizationSettingsWrap}>
                <Typography
                  ariaLabel='organizationSettingsDescription'
                  variant='body-md-regular'
                  color='var(--content-primary-primary)'
                >
                  Use organization settings
                </Typography>
                <Switch
                  value={useOrganizationSettings}
                  onChange={setUseOrganizationSettings}
                  size='middle'
                  disabled={loading}
                />
              </div>
            </div>

            <div className={styles.permissionsList}>
              {permissionsArray.map((group) => (
                <div key={group.key} className={styles.permissionGroup}>
                  <Typography
                    ariaLabel={group.label}
                    variant='body-md-semibold'
                    color='var(--content-primary-primary)'
                  >
                    {group.label}
                  </Typography>
                  <div className={styles.permissions}>
                    {group.permissions.map((permission, idx) => (
                      <div key={`${permission.key}-${idx}`} className={styles.permissionItem}>
                        <div className={styles.permissionContent}>
                          <Typography
                            ariaLabel={permission.label}
                            variant='body-md-regular'
                            color={
                              permission.disabled || useOrganizationSettings
                                ? 'var(--content-primary-disabled)'
                                : 'var(--content-primary-primary)'
                            }
                          >
                            {permission.label}

                            {permission.tooltip && (
                              <Tooltip
                                placement={'top'}
                                label={permission.tooltip.label}
                                className={styles.permissionTooltip}
                                {...(permission.tooltip?.tooltipProps
                                  ? permission.tooltip.tooltipProps
                                  : {})}
                              >
                                <Icon
                                  icon={'question'}
                                  color={'var(--content-neutral-primary)'}
                                  fontSize={14}
                                />
                              </Tooltip>
                            )}
                          </Typography>
                          <div className={styles.permissionValue}>
                            {permission.custom && (
                              <Tooltip label='Custom permission' placement='top'>
                                <Icon
                                  icon={'infoCircle'}
                                  color={'var(--content-neutral-primary)'}
                                  fontSize={14}
                                />
                              </Tooltip>
                            )}
                            {permission.valueType === 'boolean' ? (
                              <Checkbox
                                id={`${permission.key}-${idx}`}
                                checked={!!permission.value}
                                disabled={permission.disabled || loading || useOrganizationSettings}
                                onChange={() => setPermission(permission.key, !permission.value)}
                                size={20}
                              />
                            ) : (
                              <Radio
                                checked={permission.contactAccessValue === contactAccessValue}
                                disabled={permission.disabled || loading || useOrganizationSettings}
                                size='medium'
                                onChange={() => {
                                  if (permission.contactAccessValue) {
                                    setPermission(permission.key, permission.contactAccessValue)
                                  }
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Scrollbar>
        <div className={styles.actions}>
          <Button
            text='Reject'
            disabled={loading}
            onClick={openRejectModal}
            typeBtn='contained'
            contained='secondary'
            intent='destructive'
            size='medium'
          />
          <div className={styles.rightActions}>
            <Button
              text='Cancel'
              disabled={loading}
              onClick={closeModal}
              typeBtn='contained'
              contained='secondary'
              size='medium'
            />
            <Button
              text={submitButtonText}
              disabled={loading}
              onClick={onUpdate}
              typeBtn='contained'
              contained='primary'
              size='medium'
            />
          </div>
        </div>
      </div>
    )
  }
)
